import { Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Boton, EsqueletoTabla, Pill, PuntoNaranja, Tabla, TablaCabecera, TablaCelda, TablaCuerpo, TablaEncabezado, TablaFila, TituloPagina, useToast, Vacio } from "@iavmx/ui";
import { leer } from "../api";
import { Seccion } from "../componentes";
import { fechaCorta, mxn, relativo } from "../formato";
import { useSesion } from "../sesion";
import { ETIQUETA_ESTADO_COTIZACION, ETIQUETA_NIVEL, type AutorizacionPendiente, type CotizacionLista, type Nivel } from "../tipos";

const TONO: Record<string, "neutro" | "ambar" | "verde" | "azul" | "rojo"> = { borrador: "neutro", autorizacion_pendiente: "ambar", autorizada: "verde", enviada: "azul", aceptada: "verde", rechazada: "rojo", vencida: "neutro" };

export function Cotizaciones() {
  const { api, listo } = useSesion();
  const navegar = useNavigate();
  const qc = useQueryClient();
  const { avisar } = useToast();
  const lista = useQuery({ queryKey: ["cotizaciones"], queryFn: () => api.api.cotizaciones.$get().then((r) => leer<{ cotizaciones: CotizacionLista[] }>(r)), enabled: listo });
  const pendientes = useQuery({ queryKey: ["autorizaciones"], queryFn: () => api.api.cotizaciones["-"].autorizaciones.$get().then((r) => leer<{ autorizaciones: AutorizacionPendiente[]; nivel: Nivel }>(r)), enabled: listo });
  const resolver = useMutation({
    mutationFn: (v: { cotizacionId: string; aid: string; resultado: "autorizada" | "rechazada" }) => api.api.cotizaciones[":id"].autorizaciones[":aid"].resolver.$post({ param: { id: v.cotizacionId, aid: v.aid }, json: { resultado: v.resultado } }).then((r) => leer<{ ok: boolean }>(r)),
    onSuccess: (_r, v) => {
      avisar({ titulo: v.resultado === "autorizada" ? "Descuento autorizado" : "Descuento rechazado", tono: "exito" });
      void qc.invalidateQueries({ queryKey: ["autorizaciones"] });
      void qc.invalidateQueries({ queryKey: ["cotizaciones"] });
    },
    onError: (e) => avisar({ titulo: "No se pudo resolver", descripcion: e.message, tono: "error" }),
  });
  const firmables = (pendientes.data?.autorizaciones ?? []).filter((a) => a.puedoFirmar);
  const pedidas = (pendientes.data?.autorizaciones ?? []).filter((a) => !a.puedoFirmar);

  return (
    <>
      <TituloPagina
        descripcion="Sobre el catálogo, con descuento y autorización por nivel. Cada cotización la ve solo su ejecutivo y la gerencia."
        accion={
          <Boton variante="acento" tamano="sm" asChild>
            <Link to="/cotizaciones/nueva" search={{}}>
              Nueva cotización
            </Link>
          </Boton>
        }
      >
        Cotizaciones
        <PuntoNaranja />
      </TituloPagina>

      {(firmables.length > 0 || pedidas.length > 0) && (
        <Seccion titulo={firmables.length ? `Descuentos por firmar · ${firmables.length}` : "Descuentos que pediste"} className="mb-8">
          <ul className="divide-y divide-linea">
            {[...firmables, ...pedidas].map((a) => (
              <li key={a.id} className="flex flex-wrap items-center gap-3 py-3 font-ui text-sm">
                <div className="min-w-0 flex-1">
                  <Link to="/cotizaciones/$id" params={{ id: a.cotizacionId }} className="font-semibold hover:underline">
                    COT-{String(a.folio).padStart(4, "0")} · {a.cuenta}
                  </Link>
                  <div className="text-xs text-tinta-tenue">
                    {Number(a.porcentaje)} % de descuento sobre {mxn(a.total)} · pide firma de {ETIQUETA_NIVEL[a.nivelRequerido].toLowerCase()} · {a.solicitante ?? "—"} · {relativo(a.creadoEn)}
                  </div>
                </div>
                {a.puedoFirmar ? (
                  <div className="flex gap-2">
                    <Boton tamano="sm" variante="contorno" onClick={() => resolver.mutate({ cotizacionId: a.cotizacionId, aid: a.id, resultado: "rechazada" })} disabled={resolver.isPending}>
                      Rechazar
                    </Boton>
                    <Boton tamano="sm" onClick={() => resolver.mutate({ cotizacionId: a.cotizacionId, aid: a.id, resultado: "autorizada" })} disabled={resolver.isPending}>
                      Autorizar
                    </Boton>
                  </div>
                ) : (
                  <Pill tono="ambar">Esperando firma</Pill>
                )}
              </li>
            ))}
          </ul>
        </Seccion>
      )}

      {lista.isLoading && <EsqueletoTabla />}
      {lista.error && <Vacio titulo="No se pudieron cargar las cotizaciones" texto={lista.error.message} />}
      {lista.data && lista.data.cotizaciones.length === 0 && (
        <Vacio
          titulo="Sin cotizaciones todavía"
          texto="La primera se arma desde el catálogo: partidas, descuento y, si hace falta, la firma del nivel que toca."
          accion={
            <Boton variante="acento" asChild>
              <Link to="/cotizaciones/nueva" search={{}}>
                Nueva cotización
              </Link>
            </Boton>
          }
        />
      )}
      {lista.data && lista.data.cotizaciones.length > 0 && (
        <Tabla>
          <TablaCabecera>
            <TablaFila>
              <TablaEncabezado>Folio</TablaEncabezado>
              <TablaEncabezado>Cuenta</TablaEncabezado>
              <TablaEncabezado>Ejecutivo</TablaEncabezado>
              <TablaEncabezado className="text-right">Descuento</TablaEncabezado>
              <TablaEncabezado className="text-right">Total</TablaEncabezado>
              <TablaEncabezado>Estado</TablaEncabezado>
              <TablaEncabezado>Fecha</TablaEncabezado>
            </TablaFila>
          </TablaCabecera>
          <TablaCuerpo>
            {lista.data.cotizaciones.map((c) => (
              <TablaFila key={c.id} className="cursor-pointer" onClick={() => void navegar({ to: "/cotizaciones/$id", params: { id: c.id } })}>
                <TablaCelda>
                  <Link to="/cotizaciones/$id" params={{ id: c.id }} className="font-semibold hover:underline">
                    COT-{String(c.folio).padStart(4, "0")}
                  </Link>
                </TablaCelda>
                <TablaCelda>
                  <div>{c.cuenta}</div>
                  {c.oportunidad && <div className="font-ui text-xs text-tinta-tenue">{c.oportunidad}</div>}
                </TablaCelda>
                <TablaCelda>{c.ejecutivo ?? "—"}</TablaCelda>
                <TablaCelda className="text-right tabular-nums">{Number(c.descuentoPct)} %</TablaCelda>
                <TablaCelda className="text-right tabular-nums">{mxn(c.total)}</TablaCelda>
                <TablaCelda>
                  <Pill tono={TONO[c.estado] ?? "neutro"}>{ETIQUETA_ESTADO_COTIZACION[c.estado]}</Pill>
                </TablaCelda>
                <TablaCelda className="text-tinta-tenue">{fechaCorta(c.fecha)}</TablaCelda>
              </TablaFila>
            ))}
          </TablaCuerpo>
        </Tabla>
      )}
    </>
  );
}
